// src/hooks/useServiceOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  ServiceOrderStatus,
  ServiceOrderSummary,
  ServiceOrderEquipmentInput,
  ServiceOrderWithCustomer,
  ServiceOrderDetail,
} from '@/types/service';
import { toast } from 'sonner';

// ─── Listagem paginada ───────────────────────────────────────────────────────

interface UseServiceOrdersParams {
  status?: ServiceOrderStatus;
  customerId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useServiceOrders(params: UseServiceOrdersParams = {}) {
  const { company } = useAuth();
  const queryClient = useQueryClient();
  const { status, customerId, search, page = 1, pageSize = 25 } = params;

  const query = useQuery({
    queryKey: ['service-orders', company?.id, status, customerId, search, page, pageSize],
    queryFn: async () => {
      if (!company?.id) return { data: [], totalCount: 0 };

      const { data, error } = await supabase.rpc('get_service_orders_filtered', {
        p_company_id:  company.id,
        p_status:      status ?? null,
        p_customer_id: customerId ?? null,
        p_search:      search ?? null,
        p_page:        page,
        p_page_size:   pageSize,
      });

      if (error) throw error;

      const rows = (data ?? []) as ServiceOrderSummary[];
      const totalCount = rows[0]?.total_count ?? 0;

      return { data: rows, totalCount };
    },
    enabled: !!company?.id,
  });

  // Legacy mutations — mantidos para compat. com EditServiceOrderDialog (Sprint 4 remove)
  const legacyUpdateMutation = useMutation({
    mutationFn: async (variables: Record<string, unknown>) => {
      if (!company?.id) throw new Error('Empresa não identificada');
      const { id, ...rest } = variables as { id: string } & Record<string, unknown>;
      const { error } = await supabase
        .from('service_orders')
        .update(rest)
        .eq('id', id)
        .eq('company_id', company.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('Ordem de Serviço atualizada');
    },
    onError: () => {
      toast.error('Erro ao atualizar OS. Contate o suporte.');
    },
  });

  const legacyCancelMutation = useMutation({
    mutationFn: async (serviceOrderId: string) => {
      if (!company?.id) throw new Error('Empresa não identificada');
      const { error } = await supabase
        .from('service_orders')
        .update({ status: 'cancelled' })
        .eq('id', serviceOrderId)
        .eq('company_id', company.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('OS cancelada');
    },
  });

  const legacyCreateMutation = useMutation({
    mutationFn: async (_variables: Record<string, unknown>) => {
      throw new Error('Use useCreateServiceOrder() para criar OS');
    },
  });

  return {
    // Nova API
    data: query.data,
    isLoading: query.isLoading,
    // API legada (backward compat)
    serviceOrders: (query.data?.data ?? []) as unknown as ServiceOrderWithCustomer[],
    updateServiceOrder: legacyUpdateMutation.mutate,
    isUpdating: legacyUpdateMutation.isPending,
    cancelServiceOrder: legacyCancelMutation.mutate,
    isCancelling: legacyCancelMutation.isPending,
    createServiceOrder: legacyCreateMutation.mutate,
    isCreating: legacyCreateMutation.isPending,
  };
}

// ─── OS individual (para detalhe/edição) ────────────────────────────────────

export function useServiceOrder(id: string | null) {
  const { company } = useAuth();

  return useQuery({
    queryKey: ['service-order', id, company?.id],
    queryFn: async () => {
      if (!id || !company?.id) return null;

      const { data, error } = await supabase
        .from('service_orders')
        .select(`
          *,
          customers!service_orders_customer_id_fkey (
            id,
            name,
            document_number,
            phone,
            email,
            address,
            address_number,
            neighborhood,
            city,
            state,
            zip_code
          ),
          service_order_equipment (
            id,
            customer_equipment_id,
            equipment_name,
            brand,
            model,
            serial_number,
            reported_issue,
            diagnosis,
            accessories,
            warranty_status,
            position,
            extra_attributes,
            service_order_items (
              id,
              item_type,
              product_id,
              service_id,
              description,
              quantity,
              unit_price,
              total_price,
              position
            )
          ),
          service_order_photos (
            id,
            storage_path,
            caption,
            position,
            uploaded_at
          )
        `)
        .eq('id', id)
        .eq('company_id', company.id)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      return data as ServiceOrderDetail | null;
    },
    enabled: !!id && !!company?.id,
  });
}

// ─── Criar OS ────────────────────────────────────────────────────────────────

interface CreateServiceOrderParams {
  customerId: string;
  collectedBy?: string;
  notes?: string;
  internalNotes?: string;
  startDate?: string;
  equipment: ServiceOrderEquipmentInput[];
}

export function useCreateServiceOrder() {
  const { company } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateServiceOrderParams) => {
      if (!company?.id) throw new Error('Empresa não identificada');

      const { data, error } = await supabase.rpc('create_service_order_atomic', {
        p_company_id:     company.id,
        p_customer_id:    params.customerId,
        p_collected_by:   params.collectedBy ?? null,
        p_notes:          params.notes ?? null,
        p_internal_notes: params.internalNotes ?? null,
        p_start_date:     params.startDate ?? new Date().toISOString().split('T')[0],
        p_equipment:      params.equipment as unknown as string,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('Ordem de Serviço criada com sucesso');
    },
    onError: (error: Error) => {
      console.error('Erro ao criar OS:', error);
      toast.error('Erro ao criar OS. Tente novamente ou contate o suporte.');
    },
  });
}

// ─── Atualizar status ────────────────────────────────────────────────────────

export function useUpdateServiceOrderStatus() {
  const queryClient = useQueryClient();
  const { company } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: string;
    }) => {
      if (!company?.id) throw new Error('Autenticação necessária');
      const { error } = await supabase.rpc('update_service_order_atomic', {
        p_service_order_id: id,
        p_company_id:       company.id,
        p_new_status:       status,
      });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-order', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('Status atualizado');
    },
    onError: (error: Error) => {
      const msg = error.message ?? '';
      if (msg.includes('service_order_immutable')) {
        toast.error('Esta OS está finalizada e não pode ser alterada.');
      } else if (msg.includes('insufficient_privilege') || msg.includes('42501')) {
        toast.error('Você não tem permissão para esta ação.');
      } else {
        toast.error('Erro ao atualizar status. Contate o suporte.');
      }
    },
  });
}

// ─── Gerar orçamento (draft → pending_approval, atômico) ─────────────────────

interface QuoteEquipmentPayload {
  equipment_id: string;
  items: Array<{
    item_type: 'part' | 'service';
    product_id?: string;
    service_id?: string;
    description?: string;
    quantity: number;
    unit_price: number;
    position?: number;
  }>;
}

export function useGenerateServiceOrderQuote() {
  const { company } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<string, Error, { id: string; equipment: QuoteEquipmentPayload[]; validUntil?: string }>({
    mutationFn: async ({
      id,
      equipment,
      validUntil,
    }) => {
      if (!company?.id) throw new Error('Empresa não identificada');

      // RPC agora é RETURNS uuid — data contém o quotation_id gerado.
      const { data, error } = await (supabase.rpc as any)(
        'generate_service_order_quote_atomic',
        {
          p_service_order_id: id,
          p_company_id:       company.id,
          p_equipment:        equipment,
          p_valid_until:      validUntil ?? null,
        },
      );

      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-order', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('Orçamento gerado');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao gerar orçamento. Tente novamente.');
    },
  });
}

// ─── Soft delete ─────────────────────────────────────────────────────────────

export function useDeleteServiceOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      variables: string | { id: string; reason?: string },
    ) => {
      const serviceOrderId = typeof variables === 'string' ? variables : variables.id;
      const reason = typeof variables === 'string' ? undefined : variables.reason;

      // RPC faz gate (RBAC) + trava dura dos 3 sinais (venda/orçamento convertido/AR)
      const { error } = await supabase.rpc('delete_service_order_with_audit', {
        p_service_order_id: serviceOrderId,
        p_reason:           reason ?? 'Exclusão manual',
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      toast.success('OS excluída');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
